import React, { useState, useEffect, useRef } from 'react';
import { Star, Send, User, ChevronLeft, ChevronRight, Quote, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cuántas tarjetas se ven a la vez, según los breakpoints usados abajo
// (min-w-full < md, min-w-[50%] de md a lg, min-w-[33.333%] desde lg).
const getItemsPerView = () => {
  if (typeof window === 'undefined') return 1;
  if (window.innerWidth >= 1024) return 3;
  if (window.innerWidth >= 768) return 2;
  return 1;
};

const ProductReviewsCarousel = ({ productId, productName, gender = 'Hombre' }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average_rating: 0, rating_distribution: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [itemsPerView, setItemsPerView] = useState(getItemsPerView);
  const autoPlayRef = useRef(null);
  const fileInputRef = useRef(null);

  // Más allá de este índice el carrusel se desliza hacia espacio vacío (el bug original).
  const maxIndex = Math.max(0, reviews.length - itemsPerView);
  
  // Form state
  const [formData, setFormData] = useState({
    author_name: '',
    author_email: '',
    author_phone: '',
    rating: 5,
    comment: '',
    photos: []
  });
  
  const [photoPreviews, setPhotoPreviews] = useState([]);
  // accentColor para uso dinámico de clases brand
  const accentColor = gender === 'Mujer' ? 'brand-magenta' : 'brand-cobalt';

  useEffect(() => {
    if (productId) {
      loadReviews();
    }
  }, [productId]);

  // Recalcular cuántas tarjetas caben si cambia el tamaño de ventana
  useEffect(() => {
    const handleResize = () => setItemsPerView(getItemsPerView());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Si itemsPerView cambia (o cambian las reseñas) y el índice quedó fuera de rango, ajustarlo
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [itemsPerView, reviews.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-play carousel — avanza de a una tarjeta, pero nunca más allá de maxIndex
  useEffect(() => {
    if (isAutoPlaying && maxIndex > 0) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      }, 5000);
    }
    return () => clearInterval(autoPlayRef.current);
  }, [isAutoPlaying, maxIndex]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setCurrentIndex(0);
      const res = await fetch(`${API_URL}/api/public/reviews/product/${productId}`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
        setStats(data.stats || { total: 0, average_rating: 0, rating_distribution: {} });
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.photos.length > 3) {
      toast.error('Máximo 3 fotos por reseña');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} es muy grande. Máximo 5MB`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, base64]
        }));
        setPhotoPreviews(prev => [...prev, base64]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author_name.trim() || !formData.comment.trim()) {
      toast.error('Por favor completa tu nombre y comentario');
      return;
    }
    
    if (!formData.author_email.trim()) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }
    
    if (!formData.author_phone.trim()) {
      toast.error('Por favor ingresa tu número de celular');
      return;
    }
    
    if (formData.photos.length === 0) {
      toast.error('Por favor sube al menos una foto de tu perfume');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          author_name: formData.author_name.trim(),
          author_email: formData.author_email.trim(),
          author_phone: formData.author_phone.trim(),
          rating: formData.rating,
          comment: formData.comment.trim(),
          photos: formData.photos
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || '¡Gracias por tu reseña!');
        setFormData({ author_name: '', author_email: '', author_phone: '', rating: 5, comment: '', photos: [] });
        setPhotoPreviews([]);
        setShowForm(false);
      } else {
        toast.error(apiErrorMessage(data, 'Error al enviar reseña'));
      }
    } catch (error) {
      toast.error('Error al enviar reseña');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onChange = null, size = 16) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star 
          key={i} 
          size={size}
          className={`${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          onClick={interactive ? () => onChange(i) : undefined}
        />
      ))}
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(Math.min(index, maxIndex));
  };

  return (
    <div className="mt-12 pt-8 border-t border-brand-black/10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="text-center sm:text-left">
          <h3 className="text-xl md:text-2xl font-display text-brand-black">
            Reseñas de Clientes
          </h3>
          {stats.total > 0 && (
            <div className="flex items-center justify-center sm:justify-start gap-3 mt-2">
              <div className="flex items-center gap-1">
                {renderStars(Math.round(stats.average_rating))}
                <span className="text-brand-black font-bold ml-2">{stats.average_rating}</span>
              </div>
              <span className="text-brand-black/50 text-sm">({stats.total} reseñas)</span>
            </div>
          )}
        </div>
        
        <button
          onClick={() => setShowForm(!showForm)}
          className={`px-5 py-2.5 bg-${accentColor} text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 justify-center`}
        >
          <Star size={18} />
          Escribir Reseña
        </button>
      </div>

      {/* Write Review Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <form onSubmit={handleSubmit} className="bg-brand-black/[0.03] border border-brand-black/10 rounded-xl p-6 mb-8">
              <h4 className="text-lg font-semibold text-brand-black mb-4">Comparte tu experiencia</h4>
              
              {/* Rating */}
              <div className="mb-4">
                <label className="block text-brand-black/70 text-sm mb-2">Tu calificación</label>
                <div className="flex gap-1">
                  {renderStars(formData.rating, true, (rating) => setFormData(prev => ({ ...prev, rating })), 28)}
                </div>
              </div>

              {/* Name & Email & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-brand-black/70 text-sm mb-2">Tu nombre *</label>
                  <input
                    type="text"
                    value={formData.author_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                    placeholder="Ej: María G."
                    className="w-full px-4 py-2.5 bg-white border border-brand-black/15 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt"
                    required
                  />
                </div>
                <div>
                  <label className="block text-brand-black/70 text-sm mb-2">Correo electrónico *</label>
                  <input
                    type="email"
                    value={formData.author_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_email: e.target.value }))}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-2.5 bg-white border border-brand-black/15 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt"
                    required
                  />
                </div>
                <div>
                  <label className="block text-brand-black/70 text-sm mb-2">Celular *</label>
                  <input
                    type="tel"
                    value={formData.author_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_phone: e.target.value }))}
                    placeholder="442 123 4567"
                    className="w-full px-4 py-2.5 bg-white border border-brand-black/15 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt"
                    required
                  />
                </div>
              </div>

              {/* Comment */}
              <div className="mb-4">
                <label className="block text-brand-black/70 text-sm mb-2">Tu reseña *</label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder="Cuéntanos qué te pareció este perfume..."
                  rows={4}
                  className="w-full px-4 py-2.5 bg-white border border-brand-black/15 rounded-lg text-brand-black placeholder:text-brand-black/30 focus:outline-none focus:border-brand-cobalt resize-none"
                  required
                />
              </div>

              {/* Photo Upload */}
              <div className="mb-4">
                <label className="block text-brand-black/70 text-sm mb-2 flex items-center gap-2">
                  Fotos * - Mínimo 1, Máximo 3
                  <span className="px-2 py-0.5 bg-brand-magenta/10 text-brand-magenta text-[10px] rounded-full font-medium">
                    OBLIGATORIO
                  </span>
                </label>
                
                {photoPreviews.length > 0 && (
                  <div className="flex gap-3 mb-3 flex-wrap">
                    {photoPreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={preview} 
                          alt={`Preview ${index + 1}`} 
                          className="w-20 h-20 object-cover rounded-lg border border-brand-black/15"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {photoPreviews.length < 3 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors ${
                      photoPreviews.length === 0 
                        ? 'bg-brand-magenta/10 border-2 border-dashed border-brand-magenta/50 text-brand-magenta hover:bg-brand-magenta/20' 
                        : 'bg-brand-black/5 border border-brand-black/15 border-dashed text-brand-black/60 hover:text-brand-black hover:border-brand-black/30'
                    }`}
                  >
                    <Camera size={18} />
                    <span>{photoPreviews.length === 0 ? 'Subir foto (obligatorio)' : 'Agregar otra foto'}</span>
                  </button>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                
                <p className="text-brand-black/40 text-xs mt-2">
                  Formatos: JPG, PNG, WEBP. Máximo 5MB por foto.
                </p>
              </div>

              {/* Submit */}
              <div className="flex items-center justify-between">
                <p className="text-brand-black/40 text-xs">
                  Tu reseña será publicada después de ser revisada
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className={`px-6 py-2.5 bg-${accentColor} text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50`}
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send size={18} />
                      Enviar Reseña
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews Carousel */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className={`w-8 h-8 border-2 border-${accentColor} border-t-transparent rounded-full animate-spin`} />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 bg-brand-black/[0.03] border border-brand-black/[0.08] rounded-xl">
          <Star className="mx-auto mb-3 text-brand-black/20" size={40} />
          <p className="text-brand-black/60">Sé el primero en dejar una reseña</p>
          <p className="text-brand-black/40 text-sm mt-1">Comparte tu experiencia con este perfume</p>
        </div>
      ) : (
        <div className="relative">
          {/* Navigation Arrows - Desktop */}
          {maxIndex > 0 && (
            <>
              <button
                onClick={goToPrev}
                className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-brand-bone border border-brand-cobalt/20 text-brand-cobalt hover:bg-brand-cobalt/10 hover:border-brand-cobalt/40 transition-all shadow-brand-soft"
                aria-label="Anterior"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goToNext}
                className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 items-center justify-center rounded-full bg-brand-bone border border-brand-cobalt/20 text-brand-cobalt hover:bg-brand-cobalt/10 hover:border-brand-cobalt/40 transition-all shadow-brand-soft"
                aria-label="Siguiente"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Carousel */}
          <div className="overflow-hidden mx-0 md:mx-8">
            <motion.div 
              className="flex"
              animate={{ x: `-${currentIndex * (100 / itemsPerView)}%` }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {reviews.map((review, index) => (
                <div 
                  key={review.id} 
                  className="min-w-full md:min-w-[50%] lg:min-w-[33.333%] px-2"
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="h-full bg-white border border-brand-black/10 rounded-2xl p-5 md:p-6 hover:border-brand-cobalt/30 transition-all group relative overflow-hidden min-h-[220px]">
                      {/* Decorative Quote */}
                      <div className="absolute top-3 right-3 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Quote size={40} className="text-brand-cobalt" />
                      </div>

                      {/* Stars */}
                      <div className="mb-4">
                        {renderStars(review.rating)}
                      </div>

                      {/* Comment */}
                      <p className="text-brand-black/80 text-sm md:text-base leading-relaxed mb-4 line-clamp-4 italic">
                        &ldquo;{review.comment}&rdquo;
                      </p>

                      {/* Photos */}
                      {review.photos && review.photos.length > 0 && (
                        <div className="flex gap-2 mb-4">
                          {review.photos.slice(0, 2).map((photo, idx) => (
                            <a 
                              key={idx}
                              href={photo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block"
                            >
                              <img 
                                src={photo}
                                alt={`Foto ${idx + 1}`}
                                className="w-14 h-14 object-cover rounded-lg border border-brand-black/10 hover:border-brand-magenta/50 transition-colors"
                              />
                            </a>
                          ))}
                          {review.photos.length > 2 && (
                            <div className="w-14 h-14 rounded-lg bg-brand-black/5 flex items-center justify-center">
                              <span className="text-brand-black/40 text-xs">+{review.photos.length - 2}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Author */}
                      <div className="flex items-center gap-3 pt-4 border-t border-brand-black/[0.08]">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-cobalt/20 to-brand-magenta/20 flex items-center justify-center">
                          <User size={18} className="text-brand-black/50" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-brand-black font-medium text-sm truncate">
                            {review.author_name || review.customer_name || 'Cliente'}
                          </p>
                          <p className="text-brand-black/40 text-xs">
                            {formatDate(review.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Mobile Navigation */}
          {maxIndex > 0 && (
            <div className="flex md:hidden justify-center gap-2 mt-4">
              <button
                onClick={goToPrev}
                className="p-2 rounded-full bg-brand-bone border border-brand-black/10 text-brand-black/60"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={goToNext}
                className="p-2 rounded-full bg-brand-bone border border-brand-black/10 text-brand-black/60"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}

          {/* Dots Indicator — un punto por posición real del carrusel, no por reseña */}
          {maxIndex > 0 && (
            <div className="flex justify-center gap-2 mt-6 flex-wrap">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-brand-cobalt w-8'
                      : 'bg-brand-black/20 w-2 hover:bg-brand-black/40'
                  }`}
                  aria-label={`Ir a la posición ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductReviewsCarousel;
